/**
 * Fiecare plan își spune singur tot ce include.
 *
 * The feature lists are admin-editable text, and they were written the way
 * price tables usually are: "Tot ce e în Start, plus ...". That reads fine in
 * a three-column table on a wide screen, where the Start column is right
 * there. On a phone, where the plans are stacked and a parent is looking at
 * one card, it is a cross-reference to something scrolled off screen — so the
 * list that is supposed to answer "what am I paying for" instead sends them
 * somewhere else to find out.
 *
 * So a line that says "everything from <plan>" is read as what it is: a
 * declaration that the lower plan's features are included. We drop the line
 * and splice that plan's features in. Nothing is assumed about the tiers —
 * inheritance happens only where a plan itself claims it, so a list that is
 * already self-contained passes through untouched.
 */

export type PlanLike = {
  code: string;
  name: string;
  features: string[];
};

/** Words that turn a mention of another plan into "all of it is included". */
const INCLUSION_WORDS = /\b(tot|toate|totul|inclus[ăe]?|include|plus)\b/i;

const fold = (s: string) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

/**
 * Which other plan a feature line defers to, if any.
 *
 * Deliberately narrow: the line must both name another plan AND carry an
 * inclusion word. "Quiz-uri ca în campionat" names nothing and stays; "Tot ce
 * e în Start, plus rapoarte" names Start and goes.
 */
export function referencedPlan(line: string, others: PlanLike[]): PlanLike | null {
  const text = fold(line);
  if (!INCLUSION_WORDS.test(line)) return null;
  for (const other of others) {
    const name = fold(other.name);
    const code = fold(other.code);
    if (!name && !code) continue;
    // Word-boundary match so "Elite" does not match inside another word.
    const named = new RegExp(`(^|[^a-z0-9])${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`).test(text);
    const coded = new RegExp(`(^|[^a-z0-9])${code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`).test(text);
    if (named || coded) return other;
  }
  return null;
}

/**
 * The full feature list for one plan, with cross-references resolved.
 *
 * `all` must be every plan in the catalogue, in tier order. Inheritance only
 * ever looks at plans EARLIER in that list, which is what stops a pair of
 * plans referring to each other from looping forever.
 *
 * A reference pointing UP the tiers therefore resolves to nothing, and the
 * line is left exactly as the admin wrote it. That is on purpose: an odd
 * sentence on the card is better than quietly deleting text somebody typed,
 * and it stays visible in the admin screen where it can be fixed.
 */
export function expandedFeatures(plan: PlanLike, all: PlanLike[]): string[] {
  const index = all.findIndex((p) => p.code === plan.code);
  const lower = index > 0 ? all.slice(0, index) : [];

  const out: string[] = [];
  const seen = new Set<string>();
  const push = (line: string) => {
    const key = fold(line);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(line.trim());
  };

  for (const line of plan.features || []) {
    const ref = referencedPlan(line, lower);
    if (!ref) {
      push(line);
      continue;
    }
    // Splice in everything that plan includes — itself expanded, so a chain
    // (Elite → Campion → Start) resolves all the way down.
    for (const inherited of expandedFeatures(ref, all)) push(inherited);
  }

  return out;
}

/**
 * Ce pierde copilul dacă rămâne pe planul gratuit.
 *
 * The difference between this plan and the free one, which is the question a
 * parent is actually weighing. Shown as a short "extra" list rather than
 * asking them to diff two bullet lists by eye.
 */
export function featuresBeyondFree(plan: PlanLike, all: PlanLike[], freeCode = "free"): string[] {
  const free = all.find((p) => p.code === freeCode);
  if (!free) return expandedFeatures(plan, all);
  const freeSet = new Set(expandedFeatures(free, all).map(fold));
  return expandedFeatures(plan, all).filter((f) => !freeSet.has(fold(f)));
}
